import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { X, FloppyDisk } from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

function EditTaskModal({ task, projects = [], onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task.title || '',
    description: task.description || '',
    project_client: task.project_client || '',
    priority: task.priority || 'medium',
    due_date: task.due_date || '',
    tags: (task.tags || []).join(', '),
  });
  const [saving, setSaving] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredSuggestions = projects.filter(
    (p) => p.toLowerCase().includes(form.project_client.toLowerCase()) && p !== form.project_client
  );

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);

    try {
      const payload = {};
      if (form.title.trim() !== task.title) payload.title = form.title.trim();
      if (form.description.trim() !== (task.description || '')) payload.description = form.description.trim();
      if (form.project_client.trim() !== (task.project_client || '')) payload.project_client = form.project_client.trim();
      if (form.priority !== task.priority) payload.priority = form.priority;
      if (form.due_date !== (task.due_date || '')) payload.due_date = form.due_date;

      const newTags = form.tags.split(',').map((t) => t.trim()).filter((t) => t);
      const oldTags = task.tags || [];
      if (JSON.stringify(newTags) !== JSON.stringify(oldTags)) payload.tags = newTags;

      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }

      const res = await axios.put(`${API}/tasks/${task.task_id}`, payload, { withCredentials: true });
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      console.error('Failed to save task edits:', err);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-lg max-w-lg w-full"
        data-testid="edit-task-modal"
      >
        <div className="border-b border-slate-200 p-5 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Edit Task</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="close-edit-task">
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              autoFocus
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="edit-task-title"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm min-h-[80px] resize-none"
              data-testid="edit-task-description"
            />
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Project / Client</label>
            <input
              type="text"
              value={form.project_client}
              onChange={(e) => { setForm({ ...form, project_client: e.target.value }); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="e.g., MTN Nigeria"
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="edit-task-project"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md z-10 max-h-32 overflow-y-auto">
                {filteredSuggestions.map((p) => (
                  <button key={p} type="button" className="block w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                    onMouseDown={() => { setForm({ ...form, project_client: p }); setShowSuggestions(false); }}
                  >{p}</button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-3 py-2.5 w-full text-slate-900 text-sm"
                data-testid="edit-task-priority"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Due Date</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-3 py-2.5 w-full text-slate-900 text-sm"
                data-testid="edit-task-due-date"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Tags (comma-separated)</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="compliance, tax, litigation"
              className="bg-white border border-slate-200 rounded-lg focus:border-navy focus:ring-2 focus:ring-blue-100 px-4 py-2.5 w-full text-slate-900 text-sm"
              data-testid="edit-task-tags"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving || !form.title.trim()}
              className="flex-1 bg-navy text-white hover:bg-navy-hover transition-colors font-medium px-4 py-2.5 rounded-lg disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              data-testid="edit-task-save"
            >
              <FloppyDisk size={16} weight="fill" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 transition-colors font-medium px-4 py-2.5 rounded-lg text-sm"
              data-testid="edit-task-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default EditTaskModal;
